// src/pages/Settings.js — Integrations & account settings
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

const CONTACT_TYPES = [
  'LP / Investor', 'GP / Fund Manager', 'Partner Advisor',
  'Service Provider', 'Prospect', 'Other',
]

const FIRM_TYPES = [
  'LP / Investor', 'GP Client', 'Partner Advisor',
  'Service Provider', 'Other',
]

// Try to pull a company name out of an email display name.
// Recognises common separators: " - ", " | ", " @ ", and "(...)".
// Returns '' when the display name looks like just a person's name.
function extractCompanyName(displayName) {
  if (!displayName) return ''
  const dn = String(displayName).trim()

  // "Name (Company)" → Company
  const paren = dn.match(/\(([^)]+)\)\s*$/)
  if (paren) return paren[1].trim()

  // "Name - Company" / "Name | Company" / "Name @ Company" → Company
  const sep = dn.match(/\s+[-|@]\s+(.+)$/)
  if (sep) return sep[1].trim()

  // No company signal — just a personal name
  return ''
}

// ── Gmail section ─────────────────────────────────────────────────────────

function GmailSection() {
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [syncResult, setSyncResult] = useState(null)
  const [banner, setBanner] = useState(null)   // { type: 'success'|'error', msg }

  // Pick up ?gmail=connected or ?gmail=error from OAuth callback redirect
  useEffect(() => {
    const status = searchParams.get('gmail')
    const reason = searchParams.get('reason')
    if (status === 'connected') {
      setBanner({ type: 'success', msg: 'Gmail connected successfully.' })
      qc.invalidateQueries({ queryKey: ['gmailStatus'] })
    } else if (status === 'error') {
      setBanner({ type: 'error', msg: `Connection failed: ${reason || 'unknown error'}` })
    }
    // Clean query params from URL without re-rendering
    if (status) {
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['gmailStatus'],
    queryFn: () => api.get('/api/gmail/status').then(r => r.data.data),
    staleTime: 30 * 1000,
  })

  const connectMutation = useMutation({
    mutationFn: () => api.get('/api/auth/google').then(r => r.data.data),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: (err) => {
      setBanner({ type: 'error', msg: err.response?.data?.error || err.message })
    },
  })

  const syncMutation = useMutation({
    mutationFn: () => api.post('/api/gmail/sync', {}, { params: { limit: 50 } }).then(r => r.data.data),
    onSuccess: (data) => {
      setSyncResult(data)
      qc.invalidateQueries({ queryKey: ['gmailStatus'] })
      qc.invalidateQueries({ queryKey: ['interactions', 'all'] })
    },
    onError: (err) => {
      setBanner({ type: 'error', msg: err.response?.data?.error || err.message })
    },
  })

  const connected = statusData?.connected ?? false

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        {/* Gmail logo-ish icon */}
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg, #ea4335 0%, #fbbc05 50%, #34a853 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
            <path d="M0 2C0 .9.9 0 2 0h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2z" fill="white" fillOpacity=".15"/>
            <path d="M0 2l9 6 9-6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Gmail</h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Sync emails to Interactions and auto-match contacts by email address.
          </p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {statusLoading ? (
            <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          ) : (
            <span className={`badge ${connected ? 'badge-green' : 'badge-gray'}`}>
              {connected ? 'Connected' : 'Not connected'}
            </span>
          )}
        </div>
      </div>

      {/* Banner */}
      {banner && (
        <div style={{
          background: banner.type === 'success' ? 'rgba(16,185,129,0.1)' : 'var(--danger-muted)',
          border: `1px solid ${banner.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.25)'}`,
          borderRadius: 6, padding: '10px 14px', fontSize: 13,
          color: banner.type === 'success' ? '#34d399' : '#f87171',
          marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'inherit', opacity: 0.7, fontSize: 16, lineHeight: 1, padding: '0 4px',
          }}>✕</button>
        </div>
      )}

      {/* Not connected state */}
      {!statusLoading && !connected && (
        <div style={{
          background: 'var(--surface-raised)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '24px', textAlign: 'center',
        }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Connect your Google account to automatically sync emails as Interactions
            and match them to existing contacts.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
          >
            {connectMutation.isPending ? 'Redirecting…' : 'Connect Gmail'}
          </button>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
            Read-only access · No emails are sent on your behalf
          </p>
        </div>
      )}

      {/* Connected state */}
      {!statusLoading && connected && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Stats row */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
          }}>
            {[
              { label: 'Account', value: statusData?.email || '—' },
              { label: 'Last synced', value: formatDate(statusData?.last_sync) },
              { label: 'Emails imported', value: (statusData?.email_count ?? 0).toLocaleString() },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: 'var(--surface-raised)', borderRadius: 6,
                padding: '12px 14px', border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Sync result */}
          {syncResult && (
            <div style={{
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
              borderRadius: 6, padding: '12px 16px', fontSize: 13,
              display: 'flex', gap: 20, flexWrap: 'wrap',
            }}>
              <span style={{ color: '#34d399', fontWeight: 600 }}>Sync complete</span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.synced}</strong> imported
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.matched}</strong> matched to contacts
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.unmatched}</strong> unmatched
              </span>
              {syncResult.hasMore && (
                <span style={{ color: 'var(--warning)', fontWeight: 500 }}>
                  · More emails available — sync again to continue
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={() => { setSyncResult(null); syncMutation.mutate() }}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <>
                  <span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} />
                  Syncing…
                </>
              ) : 'Sync Now'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
            >
              Reconnect
            </button>
            {syncMutation.isPending && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                This may take a moment…
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Create Contact Modal (from unmatched email) ───────────────────────────

function CreateContactModal({ record, onClose, onCreated }) {
  const [form, setForm] = useState({
    name:         record.display_name || record.email_address.split('@')[0],
    contact_type: [],
    title_role:   '',
  })
  // Firm section state
  const [firmName, setFirmName]           = useState(() => extractCompanyName(record.display_name))
  const [selectedFirmId, setSelectedFirmId] = useState('')  // set when an existing firm is chosen
  const [firmType, setFirmType]           = useState('')
  const [showFirmMenu, setShowFirmMenu]   = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  const { data: firmsData = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleType(t) {
    set('contact_type', form.contact_type.includes(t)
      ? form.contact_type.filter(x => x !== t)
      : [...form.contact_type, t])
  }

  // Typeahead matches against existing firms (client-side, case-insensitive)
  const firmMatches = (() => {
    const q = firmName.trim().toLowerCase()
    if (!q) return []
    return firmsData
      .filter(f => f.name.toLowerCase().includes(q))
      .slice(0, 8)
  })()

  // An existing firm is "selected" only if the id is set AND the typed name
  // still exactly matches that firm (editing the text re-opens create mode).
  const selectedFirm = selectedFirmId
    ? firmsData.find(f => f.firm_id === selectedFirmId)
    : null
  const isExistingSelected = Boolean(
    selectedFirm && selectedFirm.name.toLowerCase() === firmName.trim().toLowerCase()
  )
  const willCreateFirm = firmName.trim().length > 0 && !isExistingSelected

  function onFirmNameChange(v) {
    setFirmName(v)
    setSelectedFirmId('')      // typing invalidates a prior selection
    setShowFirmMenu(true)
    setErr(null)
  }

  function chooseFirm(f) {
    setFirmName(f.name)
    setSelectedFirmId(f.firm_id)
    setShowFirmMenu(false)
    setErr(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return setErr('Name is required.')
    if (willCreateFirm && !firmType) {
      return setErr('Firm Type is required when creating a new firm.')
    }
    setSubmitting(true); setErr(null)
    try {
      const body = {
        name:         form.name.trim(),
        contact_type: form.contact_type.length ? form.contact_type : null,
        title_role:   form.title_role || null,
      }
      if (isExistingSelected) {
        body.firm_id = selectedFirmId
      } else if (willCreateFirm) {
        body.firm_name = firmName.trim()
        body.firm_type = [firmType]
      }
      const res = await api.post(`/api/gmail/unmatched/${record.id}/create-contact`, body)
      onCreated(res.data.data)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      overflowY: 'auto', zIndex: 1000, padding: '2rem 1rem',
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 480, margin: 'auto',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Create Contact</h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 16,
          }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{
          padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {err && (
            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
            }}>{err}</div>
          )}

          {/* Email — read-only */}
          <div className="input-group">
            <label className="input-label">Email</label>
            <input className="input" value={record.email_address} readOnly
              style={{ opacity: 0.7, cursor: 'default' }} />
          </div>

          {/* Name — editable, pre-filled */}
          <div className="input-group">
            <label className="input-label">Name *</label>
            <input className="input" value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Full name" />
          </div>

          {/* Title/Role */}
          <div className="input-group">
            <label className="input-label">Title / Role</label>
            <input className="input" value={form.title_role}
              onChange={e => set('title_role', e.target.value)}
              placeholder="e.g. Managing Director" />
          </div>

          {/* Firm — typeahead that matches existing or creates new */}
          <div className="input-group" style={{ position: 'relative' }}>
            <label className="input-label">Firm</label>
            <input
              className="input"
              value={firmName}
              onChange={e => onFirmNameChange(e.target.value)}
              onFocus={() => setShowFirmMenu(true)}
              onBlur={() => setTimeout(() => setShowFirmMenu(false), 150)}
              placeholder="Type a firm name…"
              autoComplete="off"
            />

            {/* Typeahead menu */}
            {showFirmMenu && firmMatches.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: 'var(--surface)', border: '1px solid var(--border-light)',
                borderRadius: 6, marginTop: 2, maxHeight: 200, overflowY: 'auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              }}>
                {firmMatches.map(f => (
                  <div
                    key={f.firm_id}
                    onMouseDown={() => chooseFirm(f)}   // mouseDown beats input blur
                    style={{
                      padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                      color: 'var(--text-primary)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-muted)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {f.name}
                  </div>
                ))}
              </div>
            )}

            {/* Status indicator */}
            {isExistingSelected && (
              <div style={{ fontSize: 12, color: '#4ade80', marginTop: 4 }}>
                ✓ Existing firm
              </div>
            )}
            {willCreateFirm && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Will create new firm
              </div>
            )}
          </div>

          {/* Firm Type — only when creating a new firm */}
          {willCreateFirm && (
            <div className="input-group">
              <label className="input-label">Firm Type (required for new firm)</label>
              <select className="select" value={firmType}
                onChange={e => { setFirmType(e.target.value); setErr(null) }}>
                <option value="">Select type…</option>
                {FIRM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          {/* Contact Type — multi-select chips */}
          <div className="input-group">
            <label className="input-label">Contact Type</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {CONTACT_TYPES.map(t => {
                const sel = form.contact_type.includes(t)
                return (
                  <button key={t} type="button" onClick={() => toggleType(t)}
                    style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 12,
                      border: `1px solid ${sel ? 'var(--accent)' : 'var(--border-light)'}`,
                      background: sel ? 'var(--accent-muted)' : 'transparent',
                      color: sel ? '#60a5fa' : 'var(--text-secondary)',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                    {t}
                  </button>
                )
              })}
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            paddingTop: 4, borderTop: '1px solid var(--border)', marginTop: 4,
          }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}
              disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Unmatched Emails Section ──────────────────────────────────────────────

function UnmatchedEmailsSection() {
  const qc = useQueryClient()
  const [createRecord, setCreateRecord] = useState(null)  // row → opens CreateContactModal
  const [ignoringId, setIgnoringId]     = useState(null)  // row id being ignored

  const { data, isLoading, error } = useQuery({
    queryKey: ['unmatched-emails'],
    queryFn: async () => {
      const res = await api.get('/api/gmail/unmatched')
      return res.data.data || []
    }
  })

  const ignoreMutation = useMutation({
    mutationFn: (id) => api.post(`/api/gmail/unmatched/${id}/ignore`).then(r => r.data),
    onMutate: (id) => setIgnoringId(id),
    onSettled: () => {
      setIgnoringId(null)
      qc.invalidateQueries({ queryKey: ['unmatched-emails'] })
    },
  })

  if (isLoading) return <div className="card" style={{marginTop:'24px'}}><p>Loading unmatched emails...</p></div>

  if (error) return <div className="card" style={{marginTop:'24px'}}><p style={{color:'red'}}>Error loading unmatched emails: {error.message}</p></div>

  const rows = data || []

  return (
    <div className="card" style={{marginTop: '24px'}}>
      <h3>Unmatched Emails ({rows.length})</h3>
      {rows.length === 0 ? (
        <p style={{color: '#9ca3af'}}>No unmatched email addresses</p>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Email Address</th>
                <th>Display Name</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>{row.email_address}</td>
                  <td>{row.display_name || '—'}</td>
                  <td>{row.subject ? row.subject.substring(0, 60) : '—'}</td>
                  <td>{row.date ? new Date(row.date).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setCreateRecord(row)}
                    >
                      Create Contact
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{marginLeft: '8px'}}
                      disabled={ignoringId === row.id}
                      onClick={() => ignoreMutation.mutate(row.id)}
                    >
                      {ignoringId === row.id ? 'Ignoring…' : 'Ignore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Contact modal — opened from the row's "Create Contact" button */}
      {createRecord && (
        <CreateContactModal
          record={createRecord}
          onClose={() => setCreateRecord(null)}
          onCreated={() => {
            setCreateRecord(null)
            qc.invalidateQueries({ queryKey: ['unmatched-emails'] })
            qc.invalidateQueries({ queryKey: ['contacts'] })
          }}
        />
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Settings() {
  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Settings</h1>
          <p>Manage integrations and account preferences.</p>
        </div>
      </div>

      <div style={{ maxWidth: 700 }}>
        {/* Integrations section */}
        <div style={{ marginBottom: 8 }}>
          <h2 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Integrations
          </h2>
          <GmailSection />
          <UnmatchedEmailsSection />
        </div>
      </div>
    </div>
  )
}
